import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Progress from 'core/components/progress/Progress'
import SearchBar from 'core/components/SearchBar'
import DropdownToggle from 'core/elements/dropdown/DropdownToggle'
import Checkbox from 'core/elements/input/Checkbox'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'

// Constants for performance optimization
const ITEM_HEIGHT = 52 // Height of each item in pixels (padding + content)
const BUFFER_SIZE = 5 // Number of items to render above and below viewport

export interface Item {
  label: string
  value: string
}

// Memoized radio item to prevent unnecessary re-renders
const RadioItem = React.memo<{
  item: Item
  isSelected: boolean
  onSelect: (item: Item) => void
  classes: any
}>(({ item, isSelected, onSelect, classes }) => {
  return (
    <label
      className={classes.radioLabel}
      data-testid="radio-dropdown-filter--label-1"
      onClick={() => onSelect(item)}
    >
      <Checkbox type="radio" checked={isSelected} onChange={() => onSelect(item)} />
      <Text variant="caption1">{item?.label}</Text>
    </label>
  )
})

interface Props {
  items: Item[]
  onChange: (selectedItem: Item) => void
  label?: string
  loading?: boolean
  width?: number
  value?: string
  showSearch?: boolean
  align?: 'left' | 'right'
  showLabelOnTop?: boolean
  showLabelOnLeft?: boolean
  disabled?: boolean
  error?: string
}

/**
 * @summary Global Dropdown with search implemented for tenant and region
 * It renders a dropdown with a search bar and a list of radio buttons for items
 * @param param0
 * @returns
 */
const GlobalDropdownWithSearch = ({
  label = '',
  items,
  loading,
  onChange,
  width,
  value,
  showSearch = true,
  align = 'right',
  showLabelOnTop = false,
  showLabelOnLeft = false,
  disabled = false,
  error,
}: Props) => {
  const classes = useStyles()
  const matachingItem = items?.find((item) => item.value === value)
  const selectedItem = React.useRef<Item>(matachingItem || null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [scrollTop, setScrollTop] = useState(0)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  // Set selected item on first load
  useEffect(() => {
    if (!items?.length) {
      return
    }

    selectedItem.current = items?.find((item) => item.value === value)
  }, [items, value, selectedItem])

  // Debounce search term to reduce filtering operations
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setScrollTop(0) // Reset scroll when search changes
    }, 300) // 300ms debounce

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Filter items based on debounced search term
  const filteredItems = useMemo(
    () =>
      items?.filter((item) =>
        item.label.toLowerCase().includes(debouncedSearchTerm.toLowerCase()),
      ) || [],
    [items, debouncedSearchTerm],
  )

  // Calculate virtual scrolling window
  const { visibleItems, startIndex, totalHeight, offsetY } = useMemo(() => {
    const containerHeight = 376 // maxHeight of scroll container
    const visibleItemCount = Math.ceil(containerHeight / ITEM_HEIGHT)

    // Calculate which items should be visible based on scroll position
    const startIdx = Math.floor(scrollTop / ITEM_HEIGHT)
    const endIdx = Math.min(startIdx + visibleItemCount + BUFFER_SIZE * 2, filteredItems.length)
    const actualStartIdx = Math.max(0, startIdx - BUFFER_SIZE)

    return {
      visibleItems: filteredItems.slice(actualStartIdx, endIdx),
      startIndex: actualStartIdx,
      totalHeight: filteredItems.length * ITEM_HEIGHT + 10, //Add 5px offset to bottom
      offsetY: actualStartIdx * ITEM_HEIGHT,
    }
  }, [filteredItems, scrollTop])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
    setSearchTerm('')
    setScrollTop(0) // Reset scroll position
  }, [])

  // Handle scroll to update visible window
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    setScrollTop(target.scrollTop)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [closeDropdown])

  const handleSelection = useCallback(
    (item: Item) => {
      // if item is not valid or item is already selected
      if (
        item === null ||
        item == undefined ||
        !item?.label ||
        item?.value === selectedItem?.current?.value
      ) {
        return
      }

      selectedItem.current = item
      onChange(item)
      closeDropdown()
    },
    [closeDropdown, onChange],
  )

  return (
    <div
      className={clsx(classes.dropdownContainer, {
        [classes.dropdownContainerWithLabelOnLeft]: showLabelOnLeft,
      })}
      ref={dropdownRef}
    >
      {showLabelOnTop && (
        <Text variant="caption1" component="p">
          {label}
        </Text>
      )}
      {showLabelOnLeft && <Text variant="caption1">{label}</Text>}
      <div
        className={clsx(classes.selectContainer, {
          [classes.selectContainerWithLabelOnTop]: showLabelOnTop,
        })}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <Progress loading={loading} inline>
          <div
            className={clsx(classes.inputFrame, 'inputFrame', 'dropdownInputFrame', {
              [classes.expandedInputFrame]: isOpen,
              [classes.inputFrameDisabled]: disabled,
              [classes.inputFrameError]: !!error,
            })}
          >
            <div className={clsx(classes.placeholder, 'placeholder')}>
              <Text variant="caption1">{matachingItem?.label || `Select ${label}`}</Text>
            </div>
            <DropdownToggle type="button">
              <FontAwesomeIcon solid size="sm">
                {isOpen ? 'caret-up' : 'caret-down'}
              </FontAwesomeIcon>
            </DropdownToggle>
          </div>
        </Progress>
        {isOpen && (
          <div
            className={clsx(classes.dropdownMenu, 'dropdownMenuWithSearch', {
              [classes.dropdownMenuAlignLeft]: align === 'left',
            })}
            style={width ? { width } : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            {label && !showLabelOnTop && !showLabelOnLeft && (
              <Text variant="subtitle2" className={classes.label}>
                {label}
              </Text>
            )}
            <div className={classes.radioContainer}>
              {items.length ? (
                <>
                  {/* Show search bar if items are more than 5 */}
                  {showSearch && (
                    <SearchBar
                      className={classes.searchBar}
                      searchTerm={searchTerm}
                      onSearchChange={setSearchTerm}
                      placeholder={`Search ${label}s`}
                    />
                  )}
                  {/* Show count if searching */}
                  {debouncedSearchTerm && (
                    <Text variant="caption2" className={classes.resultCount}>
                      {filteredItems.length} result{filteredItems.length !== 1 ? 's' : ''} found
                    </Text>
                  )}
                  <div
                    className={classes.radioLabelContainer}
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                  >
                    {/* Virtual scroll container with total height */}
                    <div style={{ height: totalHeight, position: 'relative' }}>
                      {/* Visible items with offset positioning */}
                      <div style={{ transform: `translateY(${offsetY}px)` }}>
                        {visibleItems?.map((item, index) => (
                          <RadioItem
                            key={item?.value}
                            item={item}
                            isSelected={selectedItem?.current?.value === item?.value}
                            onSelect={handleSelection}
                            classes={classes}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <Text variant="body2">No data found</Text>
              )}
            </div>
          </div>
        )}
      </div>
      {!!error && (
        <Text variant="body2" className={clsx(classes.error, 'error')}>
          {error}
        </Text>
      )}
    </div>
  )
}

export default GlobalDropdownWithSearch

interface StyleProps {
  width?: number
  align?: 'left' | 'right'
  showLabelOnTop?: boolean
  showLabelOnLeft?: boolean
  disabled?: boolean
  error?: string
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  dropdownMenu: {
    width: '320px',
    minWidth: '320px',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '4px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    position: 'absolute',
    zIndex: 101,
    right: 0,
    left: 'auto',
    top: 42,
    padding: theme.spacing(2),
  },
  dropdownMenuAlignLeft: {
    right: 'auto',
    left: 0,
  },
  radioContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '4px',
    margin: theme.spacing(1, 0),
  },
  label: {
    marginBottom: theme.spacing(2),
    color: theme.palette.grey[500],
  },
  menuHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    color: theme.palette.grey[500],
    '& span': {
      color: theme.palette.grey[500],
      fontWeight: 'normal',
    },
  },
  divider: {
    margin: theme.spacing(1, 0),
    background: theme.palette.grey[200],
  },
  searchBar: {
    gap: 0,
    marginBottom: theme.spacing(1),
  },
  resultCount: {
    color: theme.palette.grey[500],
    marginBottom: theme.spacing(1),
    fontStyle: 'italic',
  },
  radioLabelContainer: {
    display: 'grid',
    gap: theme.spacing(1),
    overflowY: 'auto',
    maxHeight: '376px',
    // Enable smooth scrolling
    scrollBehavior: 'smooth',
  },
  loadingMore: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  dropdownContainer: {
    position: 'relative',
    display: 'block',
    gap: 8,
    alignItems: 'center',
  },
  dropdownContainerWithLabelOnLeft: {
    display: 'flex',
  },
  inputFrame: {
    ...theme.typography.inputPlaceholder,
    marginTop: 1,
    position: 'relative',
    paddingRight: 26,
    backgroundColor: theme.components.input.frame.background,
    color: theme.components.input.frame.color,
    borderRadius: 4,
    borderColor: theme.components.input.frame.border,
    borderWidth: 1,
    borderStyle: 'solid',
    padding: theme.spacing(0, 1),
    cursor: 'pointer',
    width: 150,
  },
  inputFrameDisabled: {
    backgroundColor: 'transparent',
    cursor: 'not-allowed',
  },
  inputFrameError: {
    borderColor: theme.components.input.error,
  },
  expandedInputFrame: {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
  },
  placeholder: {
    fontWeight: 400,
    background: 'transparent',
    minHeight: 36,
    lineHeight: 2.4,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  selectContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: 0,
  },
  selectContainerWithLabelOnTop: {
    marginTop: theme.spacing(1),
  },
  error: {
    marginLeft: 4,
    marginTop: theme.spacing(1),
    color: theme.components.dropdown.error,
    lineHeight: '16px',
  },
}))
